import { ArticlesPage } from '@lib/queries';
import { getConstants, getArticles, getArticlesPage } from '@lib/sanity';
import { map } from 'lodash';
import Head from 'next/head';

import { TextBlock } from '../../components/text-block';

import type { Constants, Article } from '@lib/queries';
import type { GetServerSideProps, Metadata } from 'next';

import { ArticleCard } from '~/components/article-card';
import { Box } from '~/components/box';
import { setAppContext } from '~/components/context';
import { PageWrapper, Wrapper } from '~/components/wrapper';

type Props = { articles: Article[]; context: Constants; page: ArticlesPage };

export const metadata: Metadata = {
  description:
    'Here is your go to place to learn more about FYR and the science behind it, along with recommendations from our experts! Want to learn more about a particular topic? Let us know at contact@fyr.com.au and we will aim to get working on it!',
  robots: {
    follow: true,
    googleBot: {
      index: true,
      follow: false,
      noimageindex: true,
      'max-image-preview': 'large',
      'max-snippet': -1,
      'max-video-preview': -1,
    },
    index: false,
    nocache: true,
  },
  title: 'Blog | Find Your Rhythm',
};

const ArticlesPage = ({ articles, page, context }: Props) => {
  setAppContext(context);

  return (
    <Box>
      <Head>
        <title>Blog | Find Your Rhythm</title>
        <meta
          content="Here is your go to place to learn more about FYR and the science behind it, along with recommendations from our experts! Want to learn more about a particular topic? Let us know at contact@fyr.com.au and we will aim to get working on it!"
          name="description"
          property="og:description"
        />
        <meta
          content="index, max-image-preview=large, max-snippet=-1, max-video-preview=-1, nofollow, noimageindex"
          name="googlebot"
        />
        <meta content="follow, nocache, noindex" name="robots" />
        <meta content="Blog | Find Your Rhythm" key="title" name="title" property="og:title" />
      </Head>
      <PageWrapper>
        <TextBlock {...page.body} />
      </PageWrapper>
      <Wrapper>
        <Box marginTop="3" gridColumn="1 / span 12">
          <Box display="grid" gap="1" gridTemplateColumns={{ sm: 'cols-1', md: 'cols-2', lg: 'cols-3' }}>
            {map(articles, (article) => (
              <ArticleCard key={`article-${article.slug.current}`} {...article} />
            ))}
          </Box>
        </Box>
      </Wrapper>
    </Box>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async () => {
  const articles = await getArticles();
  const page = await getArticlesPage();
  const context = await getConstants();

  return {
    props: { articles, context, page },
  };
};

export default ArticlesPage;
